import React from 'react'
import { Button } from 'reactstrap'
import { Box, Card, CardContent, CardMedia, Chip, CircularProgress, Divider, Typography } from '@mui/material'
import { withFirebase } from '../../components/Fb'
import withAppdata from '../../components/AppConfig/withAppdata'
import { embedFor, isSpotify, nowSeconds, safeLink, splitEvents, webinarStatus } from '../../components/utils/cintoiaEvents'

// Cintoia Events (docs/features/cintoia-events.md): Cintoia's webinars and podcast episodes,
// the same for every venue and role. Published from the superadmin app; this page only reads.

const formatDateTime = (seconds) =>
  new Date(seconds * 1000).toLocaleString('fi-FI', { weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
const formatDate = (seconds) => new Date(seconds * 1000).toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', year: 'numeric' })

// The player loads only when asked: until then no third-party content (or its cookies) reaches
// the page.
function Player({ embed, title }) {
  const [playing, setPlaying] = React.useState(false)
  if (!embed) return null
  if (!playing) {
    return (
      <Button size='sm' color='primary' onClick={() => setPlaying(true)}>
        Toista
      </Button>
    )
  }
  if (isSpotify(embed)) {
    return <iframe title={title} src={embed} width='100%' height='152' frameBorder='0' allow='autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture' loading='lazy' style={{ borderRadius: 12 }} />
  }
  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 720, aspectRatio: '16 / 9' }}>
      <iframe
        title={title}
        src={embed + (embed.includes('?') ? '&' : '?') + 'autoplay=1'}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        allow='autoplay; encrypted-media; fullscreen; picture-in-picture'
        allowFullScreen
      />
    </Box>
  )
}

function OpenLink({ url, label }) {
  const href = safeLink(url)
  if (!href) return null
  return (
    <Button size='sm' color='info' href={href} target='_blank' rel='noopener noreferrer'>
      {label}
    </Button>
  )
}

function WebinarCard({ item, now }) {
  const status = webinarStatus(item, now)
  return (
    <Card variant='outlined' sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
      {safeLink(item.image) ? <CardMedia component='img' image={safeLink(item.image)} alt='' sx={{ width: { xs: '100%', sm: 200 }, objectFit: 'cover' }} /> : null}
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip size='small' label='Webinaari' />
          {status === 'live' ? <Chip size='small' color='error' label='Käynnissä' /> : null}
          {status === 'soon' ? <Chip size='small' color='warning' label='Alkamassa' /> : null}
          <Typography variant='body2' color='text.secondary'>
            {formatDateTime(item.start)}
            {item.duration ? ' · ' + item.duration + ' min' : ''}
          </Typography>
        </Box>
        <Typography variant='h6' sx={{ mt: 1 }}>
          {item.title}
        </Typography>
        {item.description ? (
          <Typography variant='body2' sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
            {item.description}
          </Typography>
        ) : null}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          <OpenLink url={item.url} label={status === 'live' ? 'Liity' : 'Ilmoittaudu / liity'} />
        </Box>
      </CardContent>
    </Card>
  )
}

function PodcastCard({ item }) {
  const embed = embedFor(item.embedurl || item.url)
  return (
    <Card variant='outlined' sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
      {safeLink(item.image) ? <CardMedia component='img' image={safeLink(item.image)} alt='' sx={{ width: { xs: '100%', sm: 160 }, objectFit: 'cover' }} /> : null}
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip size='small' label='Podcast' />
          {item.start ? (
            <Typography variant='body2' color='text.secondary'>
              {formatDate(item.start)}
            </Typography>
          ) : null}
        </Box>
        <Typography variant='h6' sx={{ mt: 1 }}>
          {item.title}
        </Typography>
        {item.description ? (
          <Typography variant='body2' sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
            {item.description}
          </Typography>
        ) : null}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Player embed={embed} title={item.title} />
          <OpenLink url={item.url} label='Avaa' />
        </Box>
      </CardContent>
    </Card>
  )
}

function PastWebinar({ item }) {
  const embed = embedFor(item.embedurl)
  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography variant='body2' color='text.secondary' sx={{ minWidth: 90 }}>
          {formatDate(item.start)}
        </Typography>
        <Typography variant='body1' sx={{ flex: 1 }}>
          {item.title}
        </Typography>
      </Box>
      {embed ? (
        <Box sx={{ mt: 1 }}>
          <Player embed={embed} title={item.title} />
        </Box>
      ) : null}
    </Box>
  )
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant='h5' sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'grid', gap: 2 }}>{children}</Box>
    </Box>
  )
}

function CintoiaEventsContainer({ appdata, firebase }) {
  const [now, setNow] = React.useState(nowSeconds())

  // the "Käynnissä" / "Alkamassa" labels and the Tulossa split follow the clock
  React.useEffect(() => {
    const timer = setInterval(() => setNow(nowSeconds()), 60 * 1000)
    return () => clearInterval(timer)
  }, [])

  // Opening the page clears the menu badge. Once per visit; a failure only leaves the badge.
  React.useEffect(() => {
    firebase.savePersonVenueManagerConfigs({ eventsseen: nowSeconds() }).catch((e) => console.error('Saving eventsseen failed', e))
  }, [firebase])

  if (!appdata) {
    return (
      <div className='content'>
        <CircularProgress size={24} />
      </div>
    )
  }
  const { upcoming, podcasts, past } = splitEvents(appdata.events || [], now)
  const empty = upcoming.length + podcasts.length + past.length === 0

  return (
    <div className='content'>
      <Box sx={{ maxWidth: 960 }}>
        <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
          Cintoian webinaarit ja podcastit.
        </Typography>
        {empty ? (
          <Typography variant='body1' color='text.secondary'>
            Ei vielä tapahtumia.
          </Typography>
        ) : null}
        {upcoming.length ? (
          <Section title='Tulossa'>
            {upcoming.map((item) => (
              <WebinarCard key={item.id} item={item} now={now} />
            ))}
          </Section>
        ) : null}
        {podcasts.length ? (
          <Section title='Podcastit'>
            {podcasts.map((item) => (
              <PodcastCard key={item.id} item={item} />
            ))}
          </Section>
        ) : null}
        {past.length ? (
          <Section title='Menneet webinaarit'>
            <Box>
              {past.map((item, i) => (
                <React.Fragment key={item.id}>
                  {i ? <Divider /> : null}
                  <PastWebinar item={item} />
                </React.Fragment>
              ))}
            </Box>
          </Section>
        ) : null}
      </Box>
    </div>
  )
}

export default withAppdata(withFirebase(CintoiaEventsContainer))
